import React, { useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import { type TooltipItem } from 'chart.js';
import type { Chart as ChartJS } from 'chart.js';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import { Chart } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';
import { KPI_PRIORITY_LABELS } from '../types';
import { KPI_BAR_SEQUENCE } from './kpiChartPalette';

const PRIORITY_BAR_COLORS = [
  'rgb(248, 113, 113)',
  'rgb(107, 114, 128)',
  'rgb(249, 115, 22)',
  'rgb(34, 197, 94)',
  'rgb(59, 130, 246)',
];

export interface KpiPrioritySlaChartProps {
  title: string;
  priorityCounts: Record<string, number>;
  slaBreachPct: Record<string, number>;
  onPriorityBarClick?: (priorityLabel: string) => void;
  /** When set, shows priority volume bars only (no SLA line) — for Change Request dashboard style. */
  variant?: 'withSla' | 'countOnly';
}

const KpiPrioritySlaChart: React.FC<KpiPrioritySlaChartProps> = ({
  title,
  priorityCounts,
  slaBreachPct,
  onPriorityBarClick,
  variant = 'withSla',
}) => {
  const chartRef = useRef<ChartJS<'bar' | 'line'>>(null);
  const counts = KPI_PRIORITY_LABELS.map((p) => priorityCounts[p] ?? 0);
  const breachPcts = KPI_PRIORITY_LABELS.map((p) => slaBreachPct[p] ?? 0);
  const maxBreach = useMemo(() => Math.max(0, ...breachPcts), [breachPcts]);
  const countOnly = variant === 'countOnly';
  const barColors = countOnly
    ? KPI_PRIORITY_LABELS.map((_, i) => KPI_BAR_SEQUENCE[i % KPI_BAR_SEQUENCE.length])
    : PRIORITY_BAR_COLORS;

  /** Right axis must include all breach % values; a fixed max (e.g. 10) breaks the line scale */
  const y1Max = useMemo(() => {
    if (maxBreach <= 0) return 10;
    const padded = maxBreach * 1.15;
    return Math.min(100, Math.ceil(padded / 5) * 5);
  }, [maxBreach]);

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onPriorityBarClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null || el.datasetIndex !== 0) return;
    const label = KPI_PRIORITY_LABELS[el.index];
    if (!label) return;
    if (countOnly && (counts[el.index] ?? 0) <= 0) return;
    onPriorityBarClick(label);
  };

  const datasets: any[] = [
    {
      label: 'Count (by priority)',
      data: counts,
      backgroundColor: barColors,
      borderRadius: 6,
      barThickness: 32,
      yAxisID: 'y',
      order: 1,
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'end',
        offset: 4,
        color: '#111827',
        font: { weight: 600, size: 11 },
        formatter: (value: number) => formatDataLabelCompact(Number(value)),
      },
    },
  ];
  if (!countOnly) {
    datasets.push({
      label: 'SLA Breach %',
      data: breachPcts,
      type: 'line',
      borderColor: 'rgb(17, 24, 39)',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      pointRadius: 5,
      pointHoverRadius: 6,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: 'rgb(17, 24, 39)',
      pointBorderWidth: 2,
      pointStyle: 'circle',
      fill: false,
      yAxisID: 'y1',
      order: 2,
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'end',
        offset: 18,
        color: '#111827',
        font: { weight: 700, size: 10 },
        formatter: (value: number, ctx: DataLabelContext) => {
          const v = Number(ctx.dataset.data[ctx.dataIndex]);
          return `${Number.isFinite(v) ? v.toFixed(1) : '0'}%`;
        },
      },
    });
  }

  return (
    <KpiChartPanel title={title} className="h-full lg:col-span-1" flexFill titleClassName="text-center">
      <div className="relative min-h-[280px] w-full flex-1">
        <Chart
          ref={chartRef}
          onClick={onPriorityBarClick ? handleClick : undefined}
          type="bar"
          data={{
            labels: [...KPI_PRIORITY_LABELS],
            datasets,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            onHover: (event, elements) => {
              if (event.native?.target instanceof HTMLElement) {
                const bar = elements.find((e) => e.datasetIndex === 0);
                const idx = bar?.index;
                const countOk =
                  !countOnly || idx === undefined || (counts[idx] ?? 0) > 0;
                event.native.target.style.cursor =
                  onPriorityBarClick && bar && countOk ? 'pointer' : 'default';
              }
            },
            interaction: { mode: 'index', intersect: false },
            layout: { padding: { top: countOnly ? 28 : 38, right: 12, left: 8, bottom: 4 } },
            plugins: {
              legend: {
                display: !countOnly,
                position: 'bottom',
                align: 'center',
                labels: {
                  boxWidth: 20,
                  boxHeight: 12,
                  padding: 18,
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    return [
                      {
                        text: 'Count (by priority)',
                        fillStyle: 'rgb(248, 113, 113)',
                        strokeStyle: 'transparent',
                        lineWidth: 0,
                        hidden: false,
                        index: 0,
                        datasetIndex: 0,
                      },
                      {
                        text: 'SLA Breach %',
                        fillStyle: '#ffffff',
                        strokeStyle: 'rgb(17, 24, 39)',
                        lineWidth: 1,
                        hidden: !ds[1],
                        index: 1,
                        datasetIndex: 1,
                      },
                    ] as any;
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    if (ctx.dataset.label === 'SLA Breach %') {
                      return `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}%`;
                    }
                    return `${ctx.dataset.label}: ${ctx.raw}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 12 } },
              },
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                border: { display: false },
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: {
                  color: '#9ca3af',
                  maxTicksLimit: 8,
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
              y1: {
                type: 'linear',
                display: !countOnly,
                position: 'right',
                min: 0,
                max: y1Max,
                border: { display: false },
                grid: {
                  drawOnChartArea: false,
                },
                ticks: {
                  stepSize: y1Max <= 20 ? 5 : undefined,
                  maxTicksLimit: 8,
                  callback: (v) => `${v}%`,
                  color: '#9ca3af',
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiPrioritySlaChart;
